import { UseMutationResult } from '@tanstack/react-query';
import {
  ColumnDefTemplate,
  CellContext,
  HeaderContext,
  PaginationState,
  RowSelectionState,
} from '@tanstack/react-table';
import {
  ChangeEvent,
  Dispatch,
  ElementType,
  ReactElement,
  ReactNode,
  RefObject,
  SetStateAction,
} from 'react';
import { FileWithPath } from 'react-dropzone';
import { MultiValue, SingleValue } from 'react-select';

interface ResultMap {
  showResult: boolean;
  data: PatientMedicationResponse[] | any[];
  error: string | null;
}

interface Users {
  name: string;
}

interface HandleLoginState {
  ResultMap: ResultMap;
  Users: Users;
}

export type HandleLoginType = 'ResultMap';

export type HandleLoginMap<T extends HandleLoginType> = HandleLoginState[T];

export enum OAUTH_LOGIN_RESPONSES {
  LOGIN_REQUIRED = 'login_required',
  CONSENT_REQUIRED = 'consent_required',
}

export interface UserType {
  name: string;
  href: string;
  current: boolean;
  subNav?: UserType[];
  mobileNavIcon?: string;
  mobileOnly?: boolean;
  mobileOnlyHide?: boolean;
  mobileName?: string;
  specificAccessRole?: string; // Is there any specfic role name only access
}

export interface RouteMap {
  Admin: UserType[];
  Patients: UserType[];
  Dispense_Tech: UserType[];
  PLO: UserType[];
  Pharmacist: UserType[];
  Doctors: UserType[];
}

export enum ROLE {
  Admin = 'Admin',
  Patients = 'Patients',
  Doctors = 'Doctors',
  Dispense_Tech = 'Dispense_Tech',
  PLO = 'PLO',
  Pharmacist = 'Pharmacist',
}

export interface PrescriptionInfra {
  id: number | undefined;
  prescription_code: string | null | undefined;
  prescription_expiry: string | null | undefined;
  product: {
    name: string | number | null | undefined;
  };
  prescribed_quantity: string | number | null | undefined;
  prescribed_repeats: string | number | null | undefined;
  prescribed_interval: string | number | null | undefined;
  remaining_repeats: number | undefined | null;
  remaining_units: number | undefined | null;
  status: string;
  token: string;
  med_chart: boolean;
}

export interface ShopifyCustomerAddress {
  address1: string;
  address2?: string | null;
  city: string;
  country: string;
  phone?: string | null;
  first_name: string;
  last_name: string;
  province: string;
  zip: string;
}

export interface OrderItemPatient {
  address: string;
  name: string;
  email: string;
  phone: string;
}

export interface OrderItemPrescription {
  token: string;
  expiry: string;
  isActive: boolean;
  prescriber: string | null;
  orginalQty: number;
  originalRepeats: number;
}

export interface OrderItemOrder {
  id: number;
  status: {
    id: number;
    name: string;
  };
  code: string;
  invoiceNumber: string;
}

export interface OrderItem {
  id: number;
  patient: OrderItemPatient;
  prescription: OrderItemPrescription;
  order: OrderItemOrder;
  orderedQty: number;
  orderedProducts: string;
  timesPreviouslyDispensed: number;
  totalUsedProducts: number;
  repeatsRemaining: number;
  qtyRemaining: number;
  monthlyLimit: number | null;
  scriptNumber: string;
  assignedTo: string | null;
  ausPostTracking: string | null;
}

export interface UpdateOrderProductsKeys {
  statusId: number;
  orderProductId: number;
}

export interface OrderListProps {
  select: {
    loading: boolean;
    data: Array<OrderSelectionOptionItem>;
    selected: Array<OrderSelectionOptionItem>;
    selectHandler: (el: OrderSelectionOptionItem[]) => void;
  };
  orderItem: Array<AdminOrderItem>;
  updateOrderStatus: (data: OrderStatusKeys) => void;
  pagination: {
    state: PaginationState;
    setter: React.Dispatch<React.SetStateAction<PaginationState>>;
    totalRecords: number;
  };
  search: SearchProps;
  setFilterDate: Dispatch<
    SetStateAction<
      | {
          from: Date | null;
          to: Date | null;
          filterBy?: string;
        }
      | undefined
    >
  >;
  setFilterApplyStatus: Dispatch<SetStateAction<boolean>>;
  setSortOrder: Dispatch<SetStateAction<SortOrderType>>;
  sort: SortOrderType;
  filterBy: string;
  firstOrderFilter: boolean;
  setFirstOrderFilter: Dispatch<SetStateAction<boolean>>;
  guestOrderFilter: boolean;
  setGuestOrderFilter: Dispatch<SetStateAction<boolean>>;
  childLoader: boolean;
}

export interface PaginationServiceProps {
  limit: number;
  page: number;
  search: string;
  filter: number[];
  filterDateFromDate?: Date;
  filterDateToDate?: Date;
  filterBy?: string;
  sort: SortOrderType;
}

export interface FrontendActionEventData {
  action: 'REFRESH_ORDERS' | 'REFRESH_TOKENS' | 'REFRESH_PRODUCTS';
}

export enum OrderStatus {
  ORDERED = 'Ordered',
  DISPENSING = 'Dispensing',
  DISPENSED = 'Dispensed',
  READY_FOR_SCAN = 'Ready for scan',
  READY_FOR_SHIPMENT = 'Ready for shipping',
  SHIPPED = 'Shipped',
  CANCEL = 'Cancel',
  REQUEST_FOR_CANCEL = 'Cancellation request',
  PAUSED = 'Paused',
  AMENDING_DISPENSE = 'Amending Dispense',
  AWAITING_PAYMENT = 'Awaiting Payment',
  AUSPOST_FAILURE = 'Auspost Failure',
  AWAITING_SAS_B = 'Awaiting SAS-B',
}

export interface ReminderLinkProps {
  href: string;
  imgSrc: string;
  title: string;
  description: string;
  className?: string;
}
export interface SvgProps {
  className?: string;
  height?: number;
  width?: number;
}

export interface ClickToActionProps {
  buttonText: string;
  className?: string;
  buttonImg?: ReactNode;
  href: string;
}
export interface ShopAllMedicationsProps {
  className?: string;
}

export type ChildrenType = {
  children?: ReactElement | undefined;
};

export interface UserDetailType {
  id?: number;
  name: string;
  role: string;
  email: string;
  user_initial: string;
  first_name: string;
  last_name: string;
  preferred_name?: string;
  internal?: boolean;
  supportingParams?: {
    isUserAllowedToOrder: boolean;
    hasValidEmail: boolean;
  };
}

export interface ShippingRate {
  price: string;
  title: string;
}

export interface ShopifyCustomerAddress {
  address1: string;
  address2?: string | null;
  city: string;
  country: string;
  phone?: string | null;
  first_name: string;
  last_name: string;
  province: string;
  zip: string;
  company?: string;
}
export interface CartApiItems {
  id: string;
  product_id: string;
  variant_id: string;
  title: string;
  quantity: number;
  line_price: string;
  price: string;
  max_quantity?: number;
  imageUrl: string;
  isProductOutOfStock: boolean;
  prescription: Prescription;
  requiresPrescription: boolean;
  description: string;
  outOfStock: OutOfStock;
  substituteForId: string;
  prelimChecksError?: string;
}

export interface CartDetailType {
  id: string;
  subtotal_price: string;
  line_items: CartApiItems[];
  web_url: string;
  total_price?: string;
  shipping_rate?: ShippingRate;
  shipping_address?: ShopifyCustomerAddress;
}

export interface CartContextType {
  cart?: CartDetailType;
  setCart: Dispatch<SetStateAction<CartDetailType | undefined>>;
}

export interface UserContextType {
  userDetail: UserDetailType;
  hasTokenRecords: boolean;
  refreshUserData: () => void;
  refreshCounts: number;
  iseRxDown: boolean;
  requestPending: PendingRequestProps;
  loadingEnable: boolean;
  setLoadingEnable: Dispatch<SetStateAction<boolean>>;
  isMobile: boolean;
  setIsMobile?: Dispatch<SetStateAction<boolean>>;
  hideDropdown: boolean;
  setHideDropdown: Dispatch<SetStateAction<boolean>>;
  activeItem: string;
  setActiveItem: Dispatch<SetStateAction<string>>;
  isMobileDropdownOpen: boolean;
  setIsMobileDropdownOpen: Dispatch<SetStateAction<boolean>>;
  nativeView: boolean;
  setNativeView: Dispatch<SetStateAction<boolean>>;
}
export interface TabProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  indicator?: boolean;
}

export interface TabItem {
  id: string;
  label: string;
  component: React.ReactNode;
  indicator?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  activeTabLabel?: string;
}
export interface TabContentProps {
  children: React.ReactNode;
  isActive: boolean;
}
export interface ColumnData {
  key: string;
  cell?: ColumnDefTemplate<CellContext<any, any>>;
  header?: ColumnDefTemplate<HeaderContext<any, any>>;
  isSortable?: boolean;
  isColumnContainsOnlyButtons?: boolean;
  width?: string;
}

export interface PaginationProps {
  state: PaginationState;
  setter: React.Dispatch<React.SetStateAction<PaginationState>>;
  totalRecords: number;
}

export interface FilterProps {
  filter: SingleValue<{
    label: string;
    value: string;
  }>;
  setFilter: Dispatch<
    SetStateAction<
      SingleValue<{
        label: string;
        value: string;
      }>
    >
  >;
}

export enum OrderTypeProps {
  ORDER = 'order',
  ONBOARD = 'onboard',
  PRODUCT = 'product',
  DOCTOR = 'doctor',
}

export interface TableProps {
  columns: Array<ColumnData>;
  data: Array<any>;
  enableSorting?: boolean;
  enableMultiSort?: boolean;
  enablePagination?: boolean;
  pagintation?: PaginationProps;
  enableSearch?: boolean;
  search?: SearchProps;
  enableRowSelection?: boolean;
  rowSelection?: {
    state: RowSelectionState;
    setter: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  };
  expandableContent?: (rowData: any) => React.ReactNode;
  order?: OrderTypeProps;
  orderBulkUpdate?: {
    state: OrderSelectionOptionItem[];
    action: () => void;
  };
  childLoader?: boolean;
  firstOrderFilter?: boolean;
  setFirstOrderFilter?: Dispatch<SetStateAction<boolean>>;
  guestOrderFilter?: boolean;
  setGuestOrderFilter?: Dispatch<SetStateAction<boolean>>;
}

export interface SelectEntity {
  id: number | string;
  name: string;
}

export interface SelectProps {
  defaultSelected: SelectEntity[];
  onChange:
    | ((value: MultiValue<{ label: string; value: number | string }>) => void)
    | undefined;
  selectBoxLabel: string;
  options: SelectEntity[];
  loading: boolean;
  placeholder?: string;
}

export interface PaginationTriggerProps {
  currentPage: PaginationState;
  pageHandler: (pageNumber: number) => void;
  totalRecords: number;
}

export interface SearchProps {
  value: string;
  changeHandler: React.Dispatch<React.SetStateAction<string>>;
  submitSearch: React.Dispatch<React.SetStateAction<number>>;
}

export interface ModalProps {
  isOpen: boolean;
  setVisibilityStatus: (status: boolean) => void;
  currentSelectedData: OrderItem | undefined;
  onPause: () => void;
  onResume: () => void;
  onChangeStatus: () => void;
  onCancel: () => void;
  onDownloadInvoice(): void;
  downloadInvoiceLoadState: boolean;
  setAusPostId: (ausPostId: string, eleId: number) => void;
  ausPostLoading: boolean;
}

export interface SelectedAusPostData {
  ausPostId: string;
  refId: number;
}

export interface OrderSelectionOptionItem {
  id: number;
  name: string;
  color_code: string;
}

export interface PreviouslyExportedOrderHistory {
  duration: string;
  status: string;
  createdAt: string;
  generatedBy: string;
  reportId: string;
}

export interface OrderReportHistoryProps {
  data: Array<PreviouslyExportedOrderHistory>;
  loading: boolean;
  isDownloaded: boolean;
  onDownloadReport: (refId: string, fileName: string) => void;
}

export interface ReportDatePickerProps {
  onExport: (
    fromDate: Date | null,
    toDate: Date | null,
    filterBy?: string,
  ) => void;
  onClear: () => void;
  btnText?: string;
  order?: boolean;
  setSortOrder?: Dispatch<SetStateAction<SortOrderType>>;
  sort?: SortOrderType;
  from?: Date;
  to?: Date;
  keyStores: {
    from: string;
    to: string;
  };
  filterBy?: string;
  setFirstOrderFilter?: Dispatch<SetStateAction<boolean>>;
  setguestOrderFilter?: Dispatch<SetStateAction<boolean>>;
}

export interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  header: string;
  children: ReactNode;
  footer?: ReactNode;
  modalSize?: string;
  modalBodyClass?: string;
}
export interface RequestOrderCancel {
  id: number;
  reason: RequestOrderCancelStates;
}

export enum RequestOrderCancelStates {
  Approve = 'approve',
  Deny = 'deny',
}

export enum CancelOrderPurpose {
  Request = 'REQUEST',
  Approved = 'APPROVED',
  Denied = 'DENIED',
}

export interface PatientOrdersTableData {
  id: number;
  createdDate: Date;
  invoiceId: number;
  totalItems: number;
  paidAmount: number;
  orderStatus: { id: number; name: string; colorCode: string };
  downloadInvoice: { orderId: number; orderCode: string };
}

export interface PatientOrderItem {
  id: number;
  invoiceId: string;
  totalAmount: number;
  createdDate: Date;
  orderStatus: { id: number; name: string; colorCode: string };
  orderProductCount: number;
  orderCode: string;
  updatedDate: Date;
}

export interface InfoFieldProps {
  label?: string;
  value: string | ReactElement;
  className?: string;
}

export interface AccordionProps {
  title: ReactElement | undefined;
  children: ReactElement | undefined;
  isOpen: boolean;
  onAccordionToggle: () => void;
}
export interface OrderInfoPatientPhone {
  mobile: string;
  home: string;
}

export interface OrderInfoShippingAddress {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string | null;
  phone: number | null;
  city: string;
  province: string;
  zipCode: string;
  specialDeliveryInstructions?: string | null;
}

export interface OrderInfoItemPatient {
  firstName: string;
  lastName: string;
  dob: Date;
  email: string;
  phone: OrderInfoPatientPhone | null;
  address: OrderInfoShippingAddress | null;
  id: number;
  isGuest?: boolean;
  company?: string;
  patientNotes?: string;
}

export interface OrderInfoItem {
  id: number;
  createdDate: Date;
  invoiceId: string;
  currentStatus: { id: number; name: string; colorCode: string };
  cancelOrderRequest: { id: number; status: string; reason?: string };
  isShipped: boolean;
  isManifested: boolean;
  patient: OrderInfoItemPatient;
  note: string | null;
  taxes: string;
  creditCardProcessingFee: string;
  shippingCost: number;
  invoiceTotal: string;
  netPayment: string;
  subTotal: string;
  refunded: string | null;
  discount: string;
  impersonatedBy: { name: string; role: string } | undefined;
  appliedGiftCards?: string;
  shipmentFailureReason?: string;
  isShipmentDeleted: boolean;
  totalOrderedQty: number;
}

export interface PatientPrimaryInfoProps {
  patient: OrderInfoItemPatient | undefined;
}

export interface OrderProductListItemProduct {
  name: string;
  id: number;
  monthlyLimit: null | number;
  requiresPrescription: boolean;
  isSASBProduct: boolean;
  sasbReferenceId?: string;
}

export interface OrderProductListItemPrescription {
  id: number;
  scriptNumber: string;
  prescribedQty: number;
  prescribedRepeats: number;
  prescriptionDate: Date | undefined;
  expiry: Date;
  status: string;
  repeatsRemaining: number;
  remainingQty: number;
  token: string;
  prescriber: string;
  productName?: string;
  prescriberId: number;
}

export interface SubstituteProduct {
  productPrescribed: Product;
  substituteFlag: boolean;
}

export interface OrderProductListItem {
  id: number;
  orderedQty: number;
  price: number;
  invalidReason?: string;
  timesPreviouslyDispensed: number;
  substituteProduct?: SubstituteProduct;
  totalProductsUsed: number;
  product: OrderProductListItemProduct;
  prescription: OrderProductListItemPrescription;
  substitute_token: string | null;
  token_while_ordering: string | null;
  subsPrescription: OrderProductListItemPrescription | null;
  containsAllowedProduct: boolean;
}

export interface OrderItemsAndActionsProps {
  patient: {
    id: number | undefined;
    email: string | undefined;
  };
  orderId: string | undefined;
  accessToken: string | undefined;
  modalProps: {
    isMModalVisible: boolean;
    setMultiModalVisible: Dispatch<SetStateAction<boolean>>;
    closeMultiModal: () => void;
  };
  setSubsFlag: Dispatch<SetStateAction<boolean>>;
  refreshParent: () => void;
  setPresciptionExpiryFlag: Dispatch<SetStateAction<boolean>>;
  tokenType: string;
  sasProductData?: Dispatch<SetStateAction<Array<OrderSasData>>>;
}

export interface SubstituteProductDataProps {
  step: number;
  data: {
    id: number;
    productId: number;
    Token: string;
    'Product Name': string;
    'Prescribed Qty': number;
    'Remaining Repeats': number;
  }[];
  token: {
    state:
      | {
          id: number;
          token: string;
          productId: number;
        }
      | undefined;
    setter: Dispatch<
      SetStateAction<
        | {
            id: number;
            token: string;
            productId: number;
          }
        | undefined
      >
    >;
  };
  substituteToken: {
    state:
      | {
          id: number;
          token: string;
        }
      | undefined;
    setter: Dispatch<
      SetStateAction<
        | {
            id: number;
            token: string;
          }
        | undefined
      >
    >;
  };
}

export enum SelectedOrderAction {
  INTIAL = 'Intial',
  ADD_AUSPOST = 'Add Auspost',
  RESUME_ORDER = 'Resume Order',
  INVOICE_DOWNLOAD = 'Invoice Download',
  DONT_RERENDER = 'No Rerender',
}

export interface SelectedOrder {
  action: SelectedOrderAction;
  orderId: number;
}

export interface LoadStates {
  isLoading: boolean;
  isError: boolean;
  reason?: string;
}

export interface OrderStatusKeys {
  orderStatusIdToUpdate: number;
  orderId: number;
}

export interface PatientOrderItem {
  id: number;
  invoiceId: string;
  orderCode: string;
  totalAmount: number;
  createdDate: Date;
  patientName: string;
  orderProductCount: number;
  orderProducts: {
    order_quantity: number;
  }[];
}

export interface AdminOrderItem extends PatientOrderItem {
  patientDob: Date;
  assignedTo: string | null;
  note: string | null;
  substituteCount: number;
  isShipmentFailed: boolean;
  allowedProductCount: number;
  isPatientFirstOrder: boolean;
  isGuest: boolean;
}

export interface TableData {
  createdDate: Date;
  orderId: string;
  patientName: string;
  dateOfBirth: Date;
  itemCount: number;
  currentStatus: { id: number; name: string };
  updateStatus: { currentSelectedId: number; name: string; orderId: number };
  assignedTo: { name: string; currentOrderStatusId: number } | null;
  moreDetails: { id: number };
  note: string | null;
  updatedDate: Date;
  isShipmentFailed: boolean;
  isPatientFirstOrder: boolean;
  isGuest: boolean;
}
export interface PatientOrderInfo {
  firstName: string;
  lastName: string;
  address: OrderInfoShippingAddress | null;
  phone: OrderInfoPatientPhone | null;
  orderId: number;
  orderCode: string;
  orderCreatedDate: Date | undefined;
  orderDispensedDate: Date | undefined;
  currentStatus: { id: number; name: string; color: string };
  cancelOrderRequest: { id: number; status: string; reason?: string };
  invoiceId: string;
  dob: Date;
  email: string;
  patientId: number;
  subTotal: string;
  creditCardProcessingFee: string;
  taxes: string;
  netPayment: string;
  invoiceTotal: string;
  refunded: string;
  shippingCost: number;
  impersonatedBy?: string;
  discount?: string;
  appliedGiftCards?: string;
  auspostId?: string;
  isGuest: boolean;
  company?: string;
}

export interface PatientOrderProductsItem {
  imgUrl: string;
  productName: string;
  orderedQty: number;
  prescriptionStatus: string;
  remainingRepeats: number;
  remainigUnits: number;
  monthlyLimit: number;
  price: number;
  requiresPrescription: boolean;
}

export interface OrderItemsPatientViewProps {
  data: Array<PatientOrderProductsItem>;
}

export interface OrderHistoryData {
  Date: string;
  Time: string;
  Action: string;
  'Updated By': string;
}

export enum InactiveTime {
  ALL_DAY = 43200000, // 12 hours
  FIFTEEN_MIN = 900000, // 15 mins
  ONE_MIN = 60000, // 1 min
}

export interface DropdownProps {
  defaultText: string;
  options: Array<OrderSelectionOptionItem>;
  defaultSelected: OrderSelectionOptionItem;
  onSelect: (selectedId: number) => void;
}

export interface QRProps {
  className?: string;
  size?: number;
  value: string;
}

export enum OrderStatusCode {
  INVALID = 0,
  ORDERED = 1,
  DISPENSING = 2,
  DISPENSED = 3,
  READY_FOR_SCAN = 4,
  READY_FOR_SHIPPING = 5,
  SHIPPED = 6,
  CANCELLED = 7,
  CANCELLATION_REQUEST = 8,
  PAUSED = 9,
  AMENDING_DISPENSE = 10,
  AWAITING_PAYMENT = 11,
  AUSPOST_FAILURE = 12,
  AWAITING_SAS_B = 13,
}
export interface TextAreaProps {
  maxLength: number;
  text: string;
  onTextChange: (text: string) => void;
  className?: string;
}

export interface TriggerGroupData {
  token: string;
  patientName: string;
  productName: string;
  toAddress?: string;
}

export interface TriggerPopup {
  data: TriggerGroupData;
  visibility: boolean;
}

export enum PrescriptionStatus {
  Available = 'available',
  Active = 'active',
  InActivate = 'inactive',
  Exhausted = 'exhausted',
  Duplicate = 'duplicate',
  Ceased = 'ceased',
  Expired = 'expired',
  Legacy = 'legacy',
  Cancelled = 'cancelled',
  Completed = 'completed',
  Dispensed = 'dispensed',
  Disabled = 'disabled',
  Stopped = 'stopped',
}

export interface OrderProductTokenList {
  opId: number;
  productName: string;
  prescriptionCode: string;
  patientId: number;
}

export interface AddTokenModalProps {
  isTokenModalOpen: boolean;
  handleAddTokenCloseClick: () => void;
  orderId?: string;
  accessToken?: string;
}

export interface InvalidProductProps {
  id: number;
  product: { name: string };
  patientId: number;
  currentStatus: OrderSelectionOptionItem;
}

export interface ReactSelectOptionProp {
  label: string;
  value: string;
}

export interface ReactSelectOptionPropTwo {
  label: string;
  value: boolean;
}

export interface PrescriptionProp {
  id: number;
  created_date: Date;
  quantity: string;
  repeats: string;
  interval: string;
  eRxToken: string;
  product: {
    label: string;
    value: number | string;
  };
  doctor: {
    label: string;
    value: number | string;
  };
  dosage: string;
  [key: string]:
    | string
    | number
    | Date
    | { label: string; value: string | number };
}

export interface PatientPrescriptionStateItem {
  id: number;
  created_date: string;
  doctor: { label: string; value: number };
  product: { label: string; value: number };
  dosage: string;
  eRxToken: string;
  quantity: string;
  repeats: string;
  schedule: string;
  interval: string;
  status: string;
}

export enum AddressType {
  Residential = 'residential',
  Business = 'business',
  Po = 'po',
  Other = 'other',
  Postal = 'postal',
}

export interface PatientAddress {
  unitNumber: string;
  streetNumber: string;
  streetName: string;
  suburb: string;
  city: string;
  stateId: number;
  postCode: number;
  isDefaultAddress: boolean;
  businessName: string;
  addressType: AddressType;
}

export interface PatientInfoState {
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredName?: string;
  dob: string;
  email: string;
  active: boolean;
  title?: string;
  gender: string;
  // ihi: string;
  medicare: string;
  medicare_reference?: string;
  medicareExpiry?: string;
  dva?: string;
  prescriptions?: Array<PatientPrescriptionStateItem>;
  // address: PatientAddress | null;
  mobile?: string;
  home?: string;
  userId: string;
  patientId: string;
  note: string;
  allowedToOrder: boolean;
  consent: boolean | null;
  internal: boolean;
  monthlyLimit?: number | null;
  vapLimit?: number;
}

export interface PatientDetailResponse {
  patientDetail: PatientDetail;
  address: PatientDetailResponseAddress;
}

export interface PatientDetailResponseAddress {
  id: number;
  unit_number: string;
  street_number: string;
  street_name: string;
  suburb: string;
  city: string;
  post_code: number;
  default: boolean;
  address_type: string;
  business_name: string;
  createdDate: string;
  updatedDate: string;
  state: PatientDetailResponseState;
  address_line_one: string;
  address_line_two: string | null;
}

export interface PatientDetailResponseState {
  id: number;
  name: string;
  abbriviation: string;
  country: PatientDetailResponseCountry;
}

export interface PatientDetailResponseCountry {
  id: number;
  name: string;
  code: number;
  alpha_2: string;
  alpha_3: string;
  createdDate: string;
  updatedDate: string;
}

export interface EmergencyContact {
  id: number;
  contact_name: string;
  contact_relationship: string;
  contact_number: string;
  consent: boolean;
}

export interface PatientDetail {
  dva: string | undefined;
  id: number;
  ihi: string;
  medicare: string;
  medicareExpiry: string;
  reference_id: null;
  consent: boolean;
  createdDate: string;
  updatedDate: string;
  user: PatientDetailResponseUser;
  prescriptions: PatientDetailResponsePrescriptionItem[];
  emergency_contacts: [] | EmergencyContact[];
  internal: boolean;
  note: string | null;
  allowedToOrder: boolean;
  monthly_limit: number;
  vape_limit: number;
}

export interface PatientDetailResponsePrescriptionItem {
  eRx_qty: string | null;
  id: number;
  prescription_code: string;
  prescription_date: string;
  prescription_expiry: string;
  prescribed_quantity: number;
  prescribed_repeats: number;
  prescribed_interval: number;
  prescribed_dosage: string;
  remaining_units: number;
  remaining_repeats: number;
  isPreferable: boolean | null;
  token: string;
  createdDate: string;
  updatedDate: string;
  med_chart: boolean;
  product: PatientDetailResponseProduct;
  status: string;
  id_rx: null;
  deletedAt: null;
  totalProductsused: number;
  timesPreviouslyDispensed: number;
  doctor: PatientDetailPrescriptionDoctor;
  comment: string;
  medication_formrtacid: string | null;
  medication_strength: string | null;
  route_administration: string | null;
  medication_brand_name: string | null;
  aip_complient_drug_name: string | null;
  isIntervalReached: number;
  // eRx_qty: string | null;
}

export interface PatientDetailResponseProduct {
  id: number;
  product_code: string;
  name: string;
  sku: string;
  description: string;
  price: string;
  weight: null;
  unit: string;
  product_type: string;
  inventory_quantity: number;
  image_url: string;
  active: boolean;
  prescription_required: boolean;
  substitutions_not_available: boolean;
  expiry_date: null;
  product_brand: string;
  createdDate: string;
  updatedDate: string;
  slug: null;
  product_schedule: null;
  monthlyLimit: number;
  productFormulation: {
    id: number;
    name: string;
  };
}

export interface PatientDetailPrescriptionDoctor {
  id: number;
  ahpra_number: null;
  hpi: string;
  prescriber_number: string;
  provider_number: string;
  reference_id: string;
  createdDate: string;
  updatedDate: string;
  user: PatientDetailResponseUser;
  internal: boolean;
}

export interface PatientDetailResponseUser {
  id: number;
  title: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  auth_token_id: string;
  email: string;
  gender: string;
  dob: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
  authentik_id: number;
  phone: PatientDetailResponsePhone;
  registered_at: string;
  is_registered: boolean;
}

export interface PatientDetailResponsePhone {
  mobile: string;
  home: string;
}

export interface PatientPrescriptionProps {
  prescription: {
    data: Array<PatientDetailPrescriptionItem>;
    setter: (ele: Array<PatientDetailPrescriptionItem>) => void;
    modalState: boolean;
    handleModal: (state: boolean) => void;
  };
  doctorAndProducts: DoctorAndProductDataParams;
  addPrescriptionModalParams: PrescriptionPopupParams;
  setTriggerPreferable: Dispatch<
    SetStateAction<
      | {
          id: number;
          isPreferable: boolean | null;
        }
      | undefined
    >
  >;
  filter: {
    state: SingleValue<{
      label: string;
      value: string;
    }>;
    setter: Dispatch<
      SetStateAction<
        SingleValue<{
          label: string;
          value: string;
        }>
      >
    >;
  };
}

export interface PatientDetailPrescriptionItemProduct {
  id: number;
  name: string;
  imgUrl: string;
  monthlyLimit: number;
  description: string;
  productFormulationId: number | undefined;
}

export interface PatientDetailPrescriptionItem {
  isPreferable: boolean | null;
  flag: boolean;
  id: number;
  createdDate: string;
  prescriedRepeats: number;
  prescriedQuantity: number;
  prescriedInterval: number;
  remainingRepeats: number;
  eRxToken: string;
  prescribedDosage: string;
  product: PatientDetailPrescriptionItemProduct;
  prescriptionCode: string;
  prescriptionDate: string;
  prescriptionExpiry: string;
  timesPreviouslyDispensed: number;
  totalProductsUsed: number;
  status: string;
  remainingUnits: number;
  doctor: string | null;
  comment: string | null;
  drugName: string;
  medicationStrength: string;
  medicationForm: string;
  brandName: string;
  routeAdmin: string;
  eRx_qty: string | null;
  isIntervalReached: number;
}

export interface DoctorAndProductsResponse {
  products: DoctorAndProductsItem[];
  doctors: DoctorAndProductsItem[];
}

export interface DoctorAndProductsItem {
  id: number;
  name: string;
}

export interface PrescriptionActionHookReturnIndexOne {
  prescriptionList: Array<PatientDetailPrescriptionItem>;
  setPrescriptionList: Dispatch<
    SetStateAction<Array<PatientDetailPrescriptionItem>>
  >;
}

export interface PrescriptionActionHookReturnIndexTwo {
  modalState: boolean;
  handleModal: (state: boolean) => void;
}

export interface DoctorAndProductDataParams {
  isLoading: boolean;
  doctorList: Array<ReactSelectOptionProp>;
  productList: Array<ReactSelectOptionProp>;
}

export interface PrescriptionPopupParams {
  item: Array<PrescriptionItemToCreate>;
  addPrescription: () => void;
  removePrescription: (id: string) => void;
  handleInput: (e: ChangeEvent<HTMLInputElement>, id: string) => void;
  handleDate: (date: Date, id: string) => void;
  handleSelect: (
    selectedOption: SingleValue<{ label: string; value: string }>,
    name: string,
    id: string,
  ) => void;
  handleSubmit: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  updatePrescriptionStatus: (
    selectedOption: SingleValue<{ label: string; value: string }>,
    id: number,
  ) => void;
}

export type PrescriptionActionHookReturn = [
  PrescriptionActionHookReturnIndexOne,
  PrescriptionActionHookReturnIndexTwo,
  DoctorAndProductDataParams,
  PrescriptionPopupParams,
  number,
  Dispatch<SetStateAction<number>>,
];

export interface PrescriptionItemToCreate {
  id: string;
  created_date: Date;
  quantity: string;
  repeats: string;
  interval: string;
  eRxToken: string;
  product: ReactSelectOptionProp;
  doctor: ReactSelectOptionProp;
  dosage: string;
}

export interface ItemsForStatusUpdate {
  selectedValue: SingleValue<{ label: string; value: string }>;
  prescriptionId: number;
  prescriptionCode: string;
}

export interface PatientMedicationResponse {
  flag: boolean;
  id: number;
  prescription_code: string;
  prescription_date: string;
  prescription_expiry: string;
  prescribed_quantity: number;
  prescribed_repeats: number;
  prescribed_interval: number;
  prescribed_dosage: string;
  remaining_units: number;
  remaining_repeats: number;
  token: string;
  createdDate: string;
  updatedDate: string;
  med_chart: boolean;
  product: PatientMedicationResponseProduct;
  patient: PatientMedicationResponsePatient;
  doctor: PatientMedicationResponseDoctor;
  status: string;
  id_rx: null;
  deletedAt: null;
  monthlyLimit: number;
  isIntervalReached: number;
}

export interface PatientMedicationResponsePatient {
  id: number;
  ihi: string;
  medicare: string;
  medicareExpiry: null;
  reference_id: string;
  consent: boolean;
  createdDate: string;
  updatedDate: string;
  user: PatientMedicationResponseUser;
  internal: boolean;
  note: null;
}

export interface PatientMedicationResponseUser {
  id: number;
  title: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  auth_token_id: string;
  email: string;
  gender: string;
  dob: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
  authentik_id: number;
  phone: PatientMedicationResponsePhone;
  registered_at: string;
  is_registered: boolean;
}

export interface PatientMedicationResponsePhone {
  mobile: string;
  home: string;
}

export interface PatientMedicationResponseProduct {
  id: number;
  product_code: string;
  name: string;
  sku: string;
  description: string;
  price: string;
  weight: null;
  unit: string;
  product_type: string;
  inventory_quantity: number;
  image_url: string;
  active: boolean;
  prescription_required: boolean;
  substitutions_not_available: boolean;
  expiry_date: null;
  product_brand: string;
  createdDate: string;
  updatedDate: string;
  slug: null;
  product_schedule: null;
}

export interface PatientMedicationResponseDoctor {
  id: number;
  ahpra_number: null;
  hpi: string;
  prescriber_number: string;
  provider_number: string;
  reference_id: string;
  createdDate: string;
  updatedDate: string;
  user: PatientMedicationResponseUser;
  internal: boolean;
}

export interface UserInfoResponse {
  id: number;
  ihi: string;
  medicare: string;
  medicareExpiry: string | null;
  dva: string | null;
  reference_id: string;
  consent: boolean;
  createdDate: string;
  updatedDate: string;
  internal: boolean;
  note: null;
  allowedToOrder: boolean;
  address: UserInfoResponseAddress;
  user: UserInfoResponseUser;
  emergency_contacts: [] | EmergencyContact[];
  hasValidMX: boolean;
}

export interface UserInfoResponseAddress {
  id: number;
  unit_number: string;
  street_number: string;
  street_name: string;
  suburb: string;
  city: string;
  post_code: number;
  default: boolean;
  address_type: string;
  business_name: string;
  createdDate: string;
  updatedDate: string;
  state: UserInfoResponseState;
}

export interface UserInfoResponseState {
  id: number;
  name: string;
  abbriviation: string;
}

export interface UserInfoResponseUser {
  id: number;
  title: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  auth_token_id: string;
  email: string;
  gender: string;
  dob: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
  authentik_id: number;
  phone: UserInfoResponsePhone;
  registered_at: string;
  is_registered: boolean;
  isNotificationEnabled: boolean;
}

export interface UserInfoResponsePhone {
  mobile: string;
  home: string;
}

export interface UserInfoResponsePhone {
  mobile: string;
  home: string;
}

export interface ToggleProps {
  label: string;
  value: boolean;
  onToggle: (val: boolean) => void;
  readonly: boolean;
}

export interface ProfileDisplayProps {
  intial: string;
  name: string;
  isNotificationEnabled: boolean;
  toggleHandler: (isChecked: boolean) => void;
}

export interface ClinicData {
  name: string;
  hospital_provider_number: string;
  hpio: string;
  email: string;
  phone: string;
  sms?: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
}
export interface ProductListProps {
  home: boolean;
}

export interface PaginatedDoctorItem {
  firstName: string;
  lastName: string;
  email: string;
  ahpraNumber: string;
  hpi: string;
  referenceId: string;
  isActive: boolean;
  internal: boolean;
}

export interface PaginatedDoctorItem {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  ahpraNumber: string;
  hpi: string;
  referenceId: string;
  prescriberNumber: string;
  isActive: boolean;
  internal: boolean;
  dateOfBirth: string;
}

export interface PatientDoctor {
  firstName: string;
  lastName: string;
  email: string;
  ahpraNumber: string;
  hpi: string;
  prescriberNumber: string;
}
export interface PanelPropInputItem {
  label: string;
  value:
    | string
    | number
    | Date
    | ReactSelectOptionProp
    | ReactSelectOptionPropTwo
    | Boolean
    | undefined;
  accessor: string;
  onUpdate: (
    ele: string | number | Date | ReactSelectOptionProp | boolean,
    accessor: string,
  ) => void;
  type: 'text' | 'number' | 'select' | 'date' | 'pre-text' | 'checkBox';
  options?: ReactSelectOptionProp[] | ReactSelectOptionPropTwo[];
  preTextContent?: string;
  isRequired?: boolean;
  error?: string;
  isDisabled?: boolean;
}

export interface PanelProps {
  name: string;
  inputs: Array<PanelPropInputItem>;
}

export interface AssociatedDoctorProps {
  accessToken: string;
  clinciId: number;
}

export interface PickListProps {
  left: {
    title: string;
    items: Array<JSX.Element>;
    loading: boolean;
    isActionEnabled: boolean;
    setSearchInputInfo: (ip: string) => void;
  };
  right: {
    title: string;
    items: Array<JSX.Element>;
    loading: boolean;
    isActionEnabled: boolean;
  };
  moveSelected: (side: 'left' | 'right') => void;
}

export interface PickListCardProps {
  name: string;
  email: string;
  id: number;
  side: 'left' | 'right';
  itemsOnTheStack: {
    side: 'left' | 'right';
    id: number;
  }[];
  onStack: () => void;
  onRemove: () => void;
}

export interface GetAssociatedDoctorItem {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  hpi: string;
  aphraNumber: string;
}

export interface SingleDoctorItem {
  id: number;
  ahpra_number: string;
  hpi: string;
  prescriber_number: string;
  provider_number: string;
  reference_id: null;
  createdDate: string;
  updatedDate: string;
  user: SingleDoctorItemUser;
  internal: boolean;
  address: SingleDoctorItemAddress;
  ap_expiry: Date;
  operatingUnderSASB: boolean;
}

export interface SingleDoctorItemAddress {
  id: number;
  unit_number: string;
  street_number: string;
  street_name: string;
  suburb: string;
  city: string;
  post_code: number;
  default: boolean;
  address_type: string;
  business_name: string;
  createdDate: string;
  updatedDate: string;
  state: SingleDoctorItemState;
}

export interface SingleDoctorItemState {
  id: number;
  name: string;
  abbriviation: string;
  country: SingleDoctorItemCountry;
}

export interface SingleDoctorItemCountry {
  id: number;
  name: string;
  code: number;
  alpha_2: string;
  alpha_3: string;
  createdDate: string;
  updatedDate: string;
}

export interface SingleDoctorItemUser {
  id: number;
  title: null;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  auth_token_id: null;
  email: string;
  gender: string;
  dob: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
  authentik_id: null;
  phone: SingleDoctorItemPhone;
  registered_at: null;
  is_registered: boolean;
  isNotificationEnabled: boolean;
}

export interface SingleDoctorItemPhone {
  mobile: string;
  home: string;
}

export interface SkeletonProps {
  className?: string;
}

export interface GoogleAutoCompleteAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface InternalUserProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  dob: undefined | Date;
  gender: string;
  role: string;
}

export interface DoctorDataProps {
  firstName: string;
  lastName: string;
  middleName: string;
  preferredName: string;
  email: string;
  mobile: string;
  phone: string;
  hpi: string;
  prescriberNumber: string;
  providerNumber: string;
  internal: ReactSelectOptionPropTwo;
  aphraNumber: string;
  apExpiry: undefined | Date;
  operatingUnderSASB: boolean;
}

export interface MonthlyLimitProductsProps {
  name: string;
  value: number;
  fill: string;
  subProducts?: string[];
}
export interface AddressAutocompletePropsState {
  city: string;
  state: string;
  country: string;
  zip: string;
  address2: string;
  streetNumber: string;
  streetName: string;
  unit: string;
  specialDeliveryInstructions?: string;
}

export interface AddressAutocompleteProps {
  addressRefValue?: string;
  address1ManualSetter: (data: string) => void;
  state: AddressAutocompletePropsState;
  freezeAddressFields: boolean;
  setter: (data: AddressAutocompletePropsState) => void;
  isForRequestToCancel?: boolean;
  // suggestions?: any;
}
export interface CartPrescriptionDetailProps {
  label: string;
  value: string;
  className?: string;
}

export interface PrescriptionDetailProps {
  label: string;
  value: string;
}

export interface SingleRowTableProps {
  headers: string[];
  values: string[][];
  headerBgColor: string;
  tableWidth: string;
}

export interface Emergencycontact {
  id: number;
  contact_name: string;
  contact_number: string;
  contact_relationship: string;
  consent: boolean;
}

export interface BrandingLoaderProps {
  loaderText: string;
}

export interface PhoneNumberProps {
  phoneNumber: string;
}

export type SortOrderType = 'ASC' | 'DESC';

export interface MultiStepModalProps {
  headerText: string;
  steps: React.ReactNode[];
  visible: boolean;
  onClose: () => void;
  modalSize: string;
  selecteToken:
    | {
        id: number;
        token: string;
      }
    | undefined;
  substituteToken?:
    | {
        id: number;
        token: string;
      }
    | undefined;
  step: {
    state: number;
    setter: Dispatch<SetStateAction<number>>;
  };
  handleNext: () => void;
  handlePrev: () => void;
  handleSubmit: () => void;
}

export interface BannerProps {
  hideBanner: boolean;
  handleBannerClose: () => void;
  className?: string;
}

export enum FilterOptions {
  CreatedDate = 'CD',
  UpdatedDate = 'UD',
}
export interface DateRangePickerProps {
  onDateChange: (start: Date | null, end: Date | null) => void;
}

export interface MonthPickerProps {
  monthRange: [Date | null, Date | null];
  onMonthChange: (range: [Date | null, Date | null]) => void;
  restrict: number;
}

export interface ToggleSwitchProps {
  option1: string;
  option2: string;
  isChecked: boolean;
  onToggle: (e: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
}

export interface OrderDateRangeFilterProps {
  onExport: (
    fromDate: Date | null,
    toDate: Date | null,
    filterBy?: string,
  ) => void;
  filterProp: {
    state: string;
    setter: Dispatch<SetStateAction<string>>;
  };
}

export interface OrderSortProps {
  sortprops: {
    state: SortOrderType;
    action: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  };
}

export interface OrderBulkUpdateProps {
  orderBulkUpdate?: {
    state: OrderSelectionOptionItem[];
    action: () => void;
  };
}

export interface Prescription {
  prescribed_quantity: number;
  prescribed_repeats: number;
  prescribed_interval: number;
  prescription_expiry: Date;
  prescribed_dosage?: string;
  prescription_date?: Date;
  medication_formrtacid?: string;
  medication_strength?: string;
  route_administration?: string;
  token: string;
  status: string;
  doctor: Doctor;
  drugName?: string;
  brandName?: string;
  routeAdmin?: string;
  medicationStrength?: string;
  medicationForm?: string;
  note?: string;
  comment?: string;
  product: Product;
  remaining_repeats: number;
}

export interface Doctor {
  id: number;
  ahpra_number: string;
  hpi: string;
  prescriber_number: string;
  provider_number: string;
  reference_id?: string | null;
  createdDate: string;
  updatedDate: string;
  user: User;
  internal: boolean;
  ap_expiry: string;
}

export interface User {
  id: number;
  title?: string | null;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_name: string;
  auth_token_id: string;
  email: string;
  gender: string;
  dob: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
  authentik_id?: string | null;
  phone: Phone;
  registered_at?: string | null;
  is_registered: boolean;
  isNotificationEnabled: boolean;
  invitedAt?: string | null;
}

interface Phone {
  mobile: string;
  home: string;
}

export interface CartOrderSummaryProps {
  cartData: CartDetailType;
  shippingLoading: boolean;
  addProductToCart: (id: string, substituteForId: string) => Promise<void>;
  removeProductFromCart: (id: string) => Promise<void>;
}

export enum OutOfStock {
  false = 1,
  quantityZero = 2,
  orderedQuantityExceeds = 3,
  orderedQuantityExceedsWithNoSubs = 4, // Ordered Quantity is equal to ordered and no Substitutions exist.
  withNoSubs = 5, // Out of Stock with no existing substituions
  withNoDoctorApprovedSub = 6,
}

export interface ProductCardsProps {
  product: any;
  addProductToCart: (id: string, substituteForId?: string) => Promise<void>;
  removeProductFromCart: (id: string) => Promise<void>;
  substitueCard?: boolean;
  isAccessories: boolean;
  allowedProduct: boolean;
}

export interface ProductQuantityIncrementProps {
  productCode: string;
  orderedQuantity: number;
  productOutOfStock: OutOfStock;
  maxQuantity?: number;
  addProductToCart: (id: string) => Promise<void>;
  removeProductFromCart: (id: string) => Promise<void>;
  minimalist?: boolean;
}

export interface DataTablePropData {
  hideHiddenColumns?: boolean;
}

interface HandleTokenRadioState {
  id: number;
  token: string;
  productId: number;
}

interface HandleTokenRadio {
  state: HandleTokenRadioState | undefined;
  setter: Dispatch<SetStateAction<HandleTokenRadioState | undefined>>;
}

interface HandleSubTokenRadio {
  state: Omit<HandleTokenRadioState, 'productId'> | undefined;
  setter: Dispatch<
    SetStateAction<Omit<HandleTokenRadioState, 'productId'> | undefined>
  >;
}

export interface DataTableProps {
  data: Array<any & DataTablePropData>;
  visibleColumns: string[];
  hiddenColumns: string[];
  imageColumn?: string;
  alwaysOpen?: boolean;
  className?: string;
  withRadioButtons?: boolean;
  handleTokenRadio?: HandleTokenRadio;
  handleSubTokenRadio?: HandleSubTokenRadio;
  step?: number;
  sasAddButon?: boolean;
}

export interface InfoBoxProps {
  content: string;
  boldContent?: string;
  className?: string;
  isIconEnable?: boolean;
  iconClassName?: string;
}

export interface OutOfStockOverlayProps {
  outOfStock: OutOfStock;
  inventoryQuantity: number;
  hasSubstitute?: boolean;
  isGuest?: boolean;
}

export interface CustomDatePickerProps {
  handleDate: (a: Date, b: number | undefined) => void;
  value?: Date;
  id?: number;
  className?: string;
  maxDate?: boolean;
  minDate?: boolean;
  readonly?: boolean;
  isSASEnabled?: boolean;
}

export interface TooltipProps {
  text: string;
  className?: string;
  id?: string;
}

export interface TrackingEvent {
  location?: string;
  description: string;
  date: string;
}

export interface TrackingHistoryData {
  Location: string;
  Description: string;
  Date: string;
}

export interface DataProps {
  data: Array<DoctorProductResponse>;
  productCategory: Array<{ label: string; value: string }>;
  productCondition: Array<{ label: string; value: string }>;
  productFormulation: Array<{ label: string; value: string }>;
}

export interface SortProps {
  sortByCategory: MultiValue<{
    label: string;
    value: string;
  }>;

  sortByFormulation: MultiValue<{
    label: string;
    value: string;
  }>;

  setSortByCategory: Dispatch<
    SetStateAction<
      MultiValue<{
        label: string;
        value: string;
      }>
    >
  >;
  sortByCondition: MultiValue<{
    label: string;
    value: string;
  }>;

  setSortByCondition: Dispatch<
    SetStateAction<
      MultiValue<{
        label: string;
        value: string;
      }>
    >
  >;
  setSortByFormulation: Dispatch<
    SetStateAction<
      MultiValue<{
        label: string;
        value: string;
      }>
    >
  >;
}

export type DoctorActionHookReturn = [
  boolean,
  DataProps,
  SearchProps,
  FilterProps,
  SortProps,
  PaginationProps,
];

export interface ProductFormulationProps {
  id: number;
  name: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
}

export interface ProductCategoryProps {
  id: number;
  name: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
}

export interface ProductImagesProps {
  id: string;
  url: string;
  createdDate: string;
  updatedDate: string;
}

export interface ProductVideosProps {
  id: string;
  url: string;
  thumbnail: string;
  createdDate: string;
  updatedDate: string;
}

export interface ProductTgaCategoryProps {
  id: number;
  name: string;
  short_name: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
}

export interface ProductDayOrNotProps {
  id: number;
  name: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
}

export enum ProductSchedule {
  ONE_YEAR = 4,
  SIX_MONTHS = 8,
}

export interface Product {
  id: number;
  product_code: string;
  name: string;
  sku: string;
  description: string;
  price: string;
  weight: number;
  unit: string;
  product_type: string;
  inventory_quantity: number;
  image_url: string;
  active: boolean;
  prescription_required: boolean;
  substitutions_not_available: boolean;
  expiry_date: string | null;
  product_brand: string;
  createdDate: string;
  updatedDate: string;
  productFormulation: ProductFormulationProps;
  tgaCategory: ProductTgaCategoryProps;
  dayOrNight: ProductDayOrNotProps;
  productCategory: ProductCategoryProps;
  productImages: Array<ProductImagesProps>;
  productVideos: Array<ProductVideosProps>;
  slug: string | null;
  product_schedule: number;
  product_strength: string;
  product_discontinued: boolean;
  substituteProduct?: Product;
  productMaxQty?: string;
}

export interface DoctorProductResponse extends Product {
  productOrderedCount: number;
}

export interface DoctorProductTableProps extends DoctorProductResponse {
  dayornight?: string;
  category?: string;
  formulation?: string;
  tga?: string;
}

export type PatientOrderHookReturn = [
  loading: boolean,
  total: number,
  orders: Array<PatientOrderDataResponse>,
  SearchProps,
  monthFilter: {
    monthRange: [Date | null, Date | null];
    setMonthRange: Dispatch<SetStateAction<[Date | null, Date | null]>>;
  },
];

export interface PatientOrderResponse {
  total: number;
  data: PatientOrderDataResponse[] | [];
}

export interface PatientOrdreOrderProductProps {
  orderQty: number;
  orderProduct: string;
  isSubsitutate: false;
  substituteProduct: string;
}
export interface PatientOrderDataResponse {
  orderId: number;
  invoiceId: string;
  createdDate: Date;
  patient: {
    name: string;
  };
  orderStatus: string;
  orderProductDetails: PatientOrdreOrderProductProps[];
}

export interface MultiDataTableCollapsibleProps {
  header: Array<string>;
  data: PatientOrderDataResponse[];
}
export interface PatientProductsProps {
  productId: number;
  name: string;
  count: number;
}

export interface PatientProductResponse {
  total: number;
  data: PatientProductsProps[] | [];
}

export type UseSocketHookReturn = [
  Array<AdminOrderItem>,
  boolean,
  number,
  number,
  number,
];

export interface AuspostFailureStatusProps {
  reason: string;
}

export interface AllowedProductProps {
  product: any;
  addProductToCart: (id: string, substituteForId?: string) => Promise<void>;
  removeProductFromCart: (id: string) => Promise<void>;
}

export interface LazyImageProp {
  key?: string;
  url: string;
  alt: string;
  className: string;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setImageUrl: Dispatch<SetStateAction<string>>;
}

export interface MainRefContextType {
  mainRef: RefObject<HTMLElement> | null;
}

export type DateFilter = Date | null;

export enum ProductTypeFileds {
  SCRIPT_RANGE = 'SCRIPT_RANGE',
  NON_SCRIPT_RANGE = 'NON_SCRIPT_RANGE',
  DEVICEANDACCESSORIES = 'DEVICEANDACCESSORIES',
  BUNDLES = 'BUNDLES',
}

export type ProductType =
  | ProductTypeFileds.SCRIPT_RANGE
  | ProductTypeFileds.NON_SCRIPT_RANGE
  | ProductTypeFileds.DEVICEANDACCESSORIES
  | ProductTypeFileds.BUNDLES;

export interface ProductsProps {
  loading: boolean;
  dataProps: DataProps;
  searchProps: SearchProps;
  filterProps: FilterProps;
  sortProps: SortProps;
  paginationProps: PaginationProps;
  type: ProductType;
}

export interface RenderProductDescriptionProps {
  description: string | undefined;
}

export enum ProductTypes {
  ACCESSORIES = 'Accessories',
  FLOWER = 'Flower',
  OIL = 'Oil',
  VAPE_CATRIDGE = 'Vape Cartridge',
  CAPSULES = 'Capsules',
  PASTILLE = 'Pastille',
  CHEWABLE_TABLET = 'Chewable Tablet',
  INHALER = 'Inhaler',
  TOPICAL = 'Topical',
  PESSARY = 'Pessary',
  BUBBLE_HASH = 'Bubble Hash',
  WAFER = 'Wafer',
  DEVICE = 'Device',
}

export enum ProductFormulation {
  TEST_KIT = 'Test Kit',
}

export enum PrescriptionDetails {
  PRODUCT_DETAILS = 'Product Details',
}

export interface PendingRequestProps {
  cancelOrderRequested: boolean;
  patientDeactivateRequested: boolean;
}
export interface MonthlyLimitPrescriptionProps {
  prescription_code: string;
  product_name: string;
  repeats: number;
  total: number;
  interval: number;
  expiry: string;
  imageUrl: string;
  isIntervalReached: number;
  consumed: number;
  status: string;
  substituteProducts: string[];
}
export interface MonthlyLimitDataProps {
  chartData: MonthlyLimitProductsProps[];
  prescriptions: MonthlyLimitPrescriptionProps[];
}
export type MonthlyLimitHookReturn = [
  boolean,
  UserDetailType,
  MonthlyLimitDataProps,
  MonthlyLimitDataProps,
];

export interface MonthlyLimitComponentProps {
  userDetail: UserDetailType;
  unit: string;
  type: string;
  products: MonthlyLimitProductsProps[];
  prescriptions: MonthlyLimitPrescriptionProps[];
}

export interface DoughnutChartProps {
  data: MonthlyLimitProductsProps[];
  unit: string;
}

export interface RetryShipmentButtonProps {
  currentOrderStatus: OrderStatusCode | undefined;
  orderId: number;
  setLoading: (status: boolean) => void;
  failureReason: string;
  hasDeletedShipment: boolean;
}

export enum MonthlyLimit {
  DRIED_LIMIT = 'monthlyLimit',
  VAP_LIMIT = 'vapLimit',
  MIN_VAP_LIMIT = 0,
  MAX_VAP_LIMIT = 8,
  MIN_DRIED_LIMIT = 0,
  MAX_DRIED_LIMIT = 100,
}

export enum Limit_Message_Enum {
  DRIED_LIMIT_MESSAGE = 'Monthly Limit cannot be greater than 100 or less than 0',
  VAP_LIMIT_MESSAGE = 'Vap Limit cannot be greater than 8 or less than 0',
}

export interface FirstOrderCheckBoxProps {
  firstOrderFilter: boolean;
  setFirstOrderFilter?: Dispatch<SetStateAction<boolean>>;
  setGuestOrderFilter?: Dispatch<SetStateAction<boolean>>;
}

export interface GuestOrderCheckBoxProps {
  guestOrderFilter: boolean;
  setFirstOrderFilter?: Dispatch<SetStateAction<boolean>>;
  setGuestOrderFilter?: Dispatch<SetStateAction<boolean>>;
}

export interface DeleteShipmentButtonProps {
  currentOrderStatus: OrderStatusCode | undefined;
  hasDeletedShipment: boolean;
  handleModalClick: () => void;
  userToken?: string;
  auspostTrackingId?: string | null;
}

export interface CreateSubstituteTokenProps {
  patientEmail: string;
  token: string;
  substituteToken: string;
  orderId: string;
  hasExpiredPrescription: boolean;
}

interface TokenProps {
  id: number;
  token: string;
  productId: number;
}

export type SelectedToken = TokenProps | undefined;

interface SubstituteTokenProps {
  id: number;
  token: string;
}

export type SubstituteToken = SubstituteTokenProps | undefined;

export interface TokenGenerationStepOneProps {
  selecteToken: SelectedToken;
  setSelectToken: Dispatch<SetStateAction<SelectedToken>>;
  substituteToken: SubstituteToken;
  setSubstituteToken: Dispatch<SetStateAction<SubstituteToken>>;
  exstingProuductList: Array<OrderProductListItem>;
  existingPrescriptionList: Array<OrderProductListItem>;
  type: string;
}

export interface OrderSasData {
  product: OrderProductListItemProduct;
  prescription: OrderProductListItemPrescription;
}

export interface AwaitingSASProps {
  orderStatusId: number;
  userGroups: boolean;
  handleOrderStatus: Dispatch<SetStateAction<number | undefined>>;
  sasProductData: Array<OrderSasData>;
  accessToken: string;
  orderId: number;
  patientId: number;
  refreshPage: () => void;
}

export interface AddSasBApprovalProps {
  token: string;
  data: Array<ApprovalStateProps>;
  orderId: number;
}

export interface ApprovalStateProps {
  productId: number;
  sasbApprovalId: string;
}

export interface BreadcrumsProps {
  data: {
    tag: ElementType | string;
    className?: string;
    href?: string;
    children: JSX.Element;
  }[];
  hasBanner?: boolean;
}

export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

export interface ProductMedia {
  url: string;
  thumbnail: string;
  type: MediaType;
}

export interface DropzoneProps {
  setAcceptedFiles: (files: Array<FileWithPath>) => void;
}

export interface DocumentCardProps {
  item: Array<DocumentCardItem>;
}

export interface DocumentCardItem {
  name: string;
  size: string;
  onDelete: () => void;
}

export interface createDosageDiaryProfileParams {
  name: string;
  file: FileWithPath;
}

export interface DosageDiaryUploaderProps {
  setUploaderVisibility: (val: boolean) => void;
  token: string;
  isUploaderVisible: boolean;
  fetchTrigger: () => void;
}

export interface DosageDiaryListItem {
  id: string;
  name: string;
  isActive: boolean;
  createdBy: string;
}

export interface DosageDiaryProfileCardProps {
  profiles: Array<DosageDiaryListItem>;
  dosageDiarySelector: (profile: DosageDiaryListItem) => void;
}

export interface AssignProductToDosageDiaryModalProps {
  isVisible: boolean;
  setVisibility: (val: boolean) => void;
  profileName: string;
  profileId: string;
  setCounter: () => void;
  invoke: number;
}

export interface FetchDosageDiaryProductQuery {
  available: boolean;
  allocated: boolean;
  name: string;
  profileId: string;
}

export interface FetcHDosageDiaryProductResult {
  id: number;
  name: string;
}

export interface AssignProductsToDosageDiaryProps {
  profileId: string;
  productList: Array<number>;
}

export interface DosageDiaryProfileContentProps {
  profile: DosageDiaryListItem;
  modalVisibility: boolean;
  token: string;
  setModalvisibility: (val: boolean) => void;
}

export interface MedicationLableProps {
  currentStatusId: number;
  isQtyAlert: boolean;
  totalQty: string;
  itemCount: number;
  setTotalQtyAlert: Dispatch<SetStateAction<boolean>>;
  setTotalQty: Dispatch<SetStateAction<string>>;
}

export interface DosageDiaryListProps {
  fetchDosageDiary: boolean;
}

export interface PatientDosageDiaryItem {
  dosageDiaryId: string;
  profileName: string;
  products: Array<string>;
}

export type Step = {
  name: string;
  id: number;
};

export interface BulkEmailStepperProps {
  steps: Array<Step>;
  currentStep: number;
}

export interface BulkEmailResult {
  templateName: string;
  templateAlias: string;
}

export interface BulkEmailConfig {
  target?: string;
  template?: string;
  active?: boolean;
  internal?: boolean;
  registered?: {
    isPicked: boolean;
    filterBy: string | null | undefined;
    startDate: Date | null | undefined;
    endDate: Date | null | undefined;
  };
  invited?: {
    isPicked: boolean;
    filterBy: string | null | undefined;
    startDate: Date | null | undefined;
    endDate: Date | null | undefined;
  };
  ignoreIfAlreadySent?: boolean;
  prescriptionStatus?: string;
  product?: string;
}

export interface TemplateSelectOptionItem {
  label: string;
  value: string;
}

export interface OptionType {
  label: string;
  value: string;
}

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface FiltersState {
  selectedPatientType: OptionType;
  status: OptionType;
  registeredFilter: OptionType;
  betweenDate: Date | null | undefined;
  selectedRegisteredDate: Date | null | undefined;
  invitedRegisteredFilter: OptionType;
  invitedBetweenDate: Date | null | undefined;
  invitedSelectedRegisteredDate: Date | null | undefined;
  ignoreIfAlreadySent: boolean;
  isRegistered: boolean;
  isInvited: boolean;
  template: OptionType;
  userType: OptionType;
  prescriptionStatus: OptionType;
  product: OptionType;
}

export enum BulkEmailTaskTarget {
  Patient = 'Patient',
  Prescription = 'Prescription',
}

export enum BulkEmailTaskDateFilterBy {
  On = 'ON',
  Before = 'BEFORE',
  After = 'AFTER',
  Between = 'BETWEEN',
}

export enum BulkEmailUserType {
  active = 'active',
  inActive = 'inActive',
}

export enum BulkEmailUserStatus {
  internal = 'internal',
  external = 'external',
}

export enum SubNavItem {
  MEDICATION_INFORMATION = 'medication information',
  DOSING_DIARIES = 'dosing diaries',
  ORDER_HISTORY = 'order history',
  MONTHLY_LIMITS = 'monthly limits',
}

export interface BulkEmaillPreviewProps {
  token: string;
  filters: FiltersState;
  taskExecutionId: string;
  isvalidCSV: (valid: boolean) => void;
  isCsvDownloaded: (valid: boolean) => void;
}

export interface BulkEmaiUserSelectionProps {
  token: string;
  setValidTemplate: Dispatch<SetStateAction<boolean>>;
  filters: FiltersState;
  setFilters: Dispatch<SetStateAction<FiltersState | undefined>>;
}

export interface BulkEmailDetailSelectProps {
  filters: FiltersState;
  setFilters: Dispatch<SetStateAction<FiltersState | undefined>>;
}

export interface NonOrderableProductItem {
  id: number;
  product_code: string;
  name: string;
  sku: string;
  description: string;
  price: string;
  weight: number;
  unit: string;
  product_type: string;
  inventory_quantity: number;
  image_url: string;
  active: boolean;
  prescription_required: boolean;
  substitutions_not_available: boolean;
  expiry_date: null;
  product_brand: string;
  eta: null;
  createdDate: Date;
  updatedDate: Date;
  slug: null;
  product_schedule: number;
  product_strength: null;
  product_discontinued: boolean;
}

export interface NonOrderableProductProps {
  data: Array<NonOrderableProductItem>;
  enableShopAllMedications: boolean;
}

export interface ProductBrandResult {
  product_brand: string;
}

export interface NonScriptRangeProps {
  addToCartMutation: UseMutationResult<null, Error, string, unknown>;
  removeFromCartMutation: UseMutationResult<void, Error, string, unknown>;
  loading: boolean;
}

export interface UseModalDismissalProps {
  permanentlyDismissedKey: string;
  temporarilyDismissedKey: string;
  isLoading?: boolean;
}
export interface ListMedicationsProps {
  products: Array<any>;
  addProductToCart: (
    id: string,
    substituteForId?: string | undefined,
  ) => Promise<void>;
  removeProductFromCart: (id: string) => Promise<void>;
  nonOrderable?: NonOrderableProductItem[] | null | undefined;
  isHome?: boolean;
}

export interface GuestCartResponse {
  success: boolean;
  message?: string;
  cart?: CartDetailType;
  status?: number;
}

export interface AddGuestCartResponse {
  success: boolean;
  status?: number;
  data?: {
    message?: string;
    cart?: any;
  };
  message?: string;
}

export interface GuestNonScriptRangeProps {
  addToCartMutation: UseMutationResult<
    AddGuestCartResponse,
    Error,
    string,
    unknown
  >;
  removeFromCartMutation: UseMutationResult<
    AddGuestCartResponse,
    Error,
    string,
    unknown
  >;
  loading: boolean;
}

export interface GuestNonScriptRangeBundlesProps {
  addToCartMutation: UseMutationResult<
    AddGuestCartResponse,
    Error,
    string,
    unknown
  >;
  removeFromCartMutation: UseMutationResult<
    AddGuestCartResponse,
    Error,
    string,
    unknown
  >;
  loading: boolean;
}

export interface GuestNonScriptRangeProductsProps {
  addToCartMutation: UseMutationResult<
    AddGuestCartResponse,
    Error,
    string,
    unknown
  >;
  removeFromCartMutation: UseMutationResult<
    AddGuestCartResponse,
    Error,
    string,
    unknown
  >;
  loading: boolean;
}

export interface ShippingInfo {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  specialDeliveryInstructions?: string;
  email: string;
  mobile: string;
  userId?: number;
}

export interface GuestShippingDetailsProps {
  cart: any;
  callCart: any;
  shippingEditDetailEnabled: any;
  editShippingEnabled: boolean;
}

export const UserOrderFilters: Array<SelectEntity> = [
  { id: 1, name: 'Guest' },
  { id: 2, name: 'Patients' },
];

export interface PersonalInfo {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
}

export interface GuestPersonalInfoProps {
  onSave?: (personalInfo: PersonalInfo) => void;
  isUserCreated?: boolean;
  isLoading?: boolean;
}

export interface GuestCartItem {
  product_id: string;
  quantity: number;
}

export interface GuestCartContextType {
  cartItems: GuestCartItem[];
  cartCount: number;
  refreshCart: () => Promise<void>;
  getProductQuantity: (productId: string) => number;
}

export interface AddToCartButtonProps {
  product: Product;
  maxQty?: number | undefined;
  substituteProduct: Product | undefined;
}

export enum PatientSubRole {
  InternalPatient = 'Internal_Patient',
  ExternalPatient = 'External_Patient',
}

export interface SubNavItemInterface {
  name: string;
  href: string;
  icon: ReactElement;
}

export interface SubNavBarProps {
  items: SubNavItemInterface[];
  isVisible: boolean | undefined;
  isMobile: boolean;
}

export interface NavItemProps {
  element: UserType;
  cartCount?: number;
  onClick?: () => void;
  isDropdownOpen: boolean;
  isActive: boolean;
}
export interface CreditBalanceProps {
  credits: number;
  isCreditsLoading: boolean;
}

export enum OrderCancelationReason {
  SHIPPING_ADDRESS_INCORRECT = 'I realised my shipping address is incorrect',
  NO_LONGER_NEEDED = 'I no longer need this medication',
  WRONG_PRODUCT_OR_STRENGTH = 'I ordered the wrong product or strength',
  SPEAK_WITH_DOCTOR = "I'd like to speak with my doctor before proceeding",
  OTHER_LABEL = 'Other (please specify)',
  OTHER = 'Other',
}

export enum CancelOrderRequestMessage {
  CANCEL_APPROVED_MESSAGE = 'The order cancellation request submitted by the patient was reviewed. The order has been cancelled due to:',
  CANCEL_REJECTED_MESSAGE = 'The order cancellation request submitted by the patient was reviewed. The order has been resumed due to:',
}

export type CancelOrderRequestMessageType = (status: string) => string;

export enum RequestOrderCancellationQuestion {
  CANCEL_ORDER_QUESTION = 'Are you sure want to Cancel Order ?',
  RESUME_ORDER_QUESTION = 'Do you wish to resume this order ?',
}

export interface YesNoButtonProps {
  yesOnClick: () => void;
  noOnClick: () => void;
  yesLabel: string;
  noLabel: string;
  yesClassName?: string;
  noClassName?: string;
}

export interface ContactDetails {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  message: string;
}
export type SortResultType = (
  a: boolean,
  b: boolean,
  isAscending: boolean,
) => number;
export interface WrapEmailProps {
  email: string;
  className?: string;
}
export type WrapEmailType = (props: WrapEmailProps) => JSX.Element;
export interface WrapAddressProps {
  address: OrderInfoShippingAddress | null;
  className?: string;
}
export type WrapAddressType = (props: WrapAddressProps) => JSX.Element;

export enum SortOptions {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface MonthlyLimitResult {
  date: string;
  sum: number;
  vape_sum: number;
}
